/* eslint-disable react-refresh/only-export-components -- контекст и useCity в одном модуле */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { cityApi, City } from "@/shared/api/cityApi";

export const CITY_STORAGE_KEY = "selected_city";

interface CityContextType {
  selectedCity: City | null;
  availableCities: City[];
  isLoading: boolean;
  error: string | null;
  changeCity: (city: City) => void;
  refreshCities: () => Promise<void>;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

export const CityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await cityApi.getCities();
      if (data.length > 0) {
        setAvailableCities(data);
        const savedCity = localStorage.getItem(CITY_STORAGE_KEY);
        if (savedCity) {
          try {
            const parsed = JSON.parse(savedCity);
            const exists = data.some((c: City) => c.id === parsed.id);
            setSelectedCity(exists ? parsed : data[0]);
          } catch {
            setSelectedCity(data[0]);
          }
        } else {
          setSelectedCity(data[0]);
        }
      } else {
        localStorage.removeItem(CITY_STORAGE_KEY);
        setAvailableCities([]);
        setSelectedCity(null);
        setError("Нет доступных торговых объектов");
      }
    } catch {
      setError("Ошибка загрузки городов");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const changeCity = useCallback((city: City) => {
    setSelectedCity(city);
    try {
      localStorage.setItem(CITY_STORAGE_KEY, JSON.stringify(city));
      window.dispatchEvent(new Event("storage"));
    } catch (error) {
      console.error("Failed to save city:", error);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CITY_STORAGE_KEY && e.newValue) {
        try {
          const newCity = JSON.parse(e.newValue);
          setSelectedCity(newCity);
        } catch (error) {
          console.error("Failed to parse city from storage event:", error);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return (
    <CityContext.Provider
      value={{
        selectedCity,
        availableCities,
        isLoading,
        error,
        changeCity,
        refreshCities: fetchCities,
      }}
    >
      {children}
    </CityContext.Provider>
  );
};

export const useCity = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error("useCity must be used within a CityProvider");
  }
  return context;
};
